<template>
    <FormElement>
        <template #start>
            <FormLabel
                ref="emailLabel"
                class="o-formEmailLoginHint__label"
            >
                E-Mail-Adresse *
            </FormLabel>
        </template>
        <FormInput
            v-model.trim="rawEmail"
            type="text"
            placeholder="E-Mail-Adresse"
            data-qa="email"
            data-gtm-element="AP_F: Personal Data"
            data-gtm-element-detail="Email-Address"
            :status="v$.email.$error && !v$.email.$pending ? 'error' : ''"
            :show-valid-icon="!v$.email.$invalid && !v$.email.$pending"
            :class="{ 'o-formEmailLoginHint__input--active': isActive }"
            @blur="blur"
            @focus="focus"
        />
        <WebOnly>
            <div
                v-if="isActive && !auth.isLoggedIn && !showEmailAlreadyInUseBox"
                v-track-visibility="{
                    element: 'AP_F: Form Login Hint',
                }"
                class="k-c-infoBox k-c-infoBox--hint o-formEmailLoginHint__hint"
                data-qa="login hint"
            >
                <div class="k-c-infoBox__wrap o-formEmailLoginHint__wrap">
                    <div class="k-c-infoBox__content o-formEmailLoginHint__content">
                        Bist du schon karriere.at-User?
                        <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->
                        <RouterLink
                            class="k-c-infoBox__link o-formEmailLoginHint__link--nowrap"
                            :to="{ name: 'login' }"
                            data-qa="login link"
                            tabindex="-1"
                            data-gtm-element="AP_F: Mail Hint Link"
                            @mouseleave="mouseLeave"
                            @mouseenter="mouseEnter"
                        >
                            Anmelden und Daten laden
                        </RouterLink>
                    </div>
                </div>
            </div>
        </WebOnly>
        <template #end>
            <AppInfoBox
                v-if="showEmailAlreadyInUseBox"
                v-track-visibility="{
                    element: 'AP_F: Validation-Error',
                    elementDetail: 'existing email logged' + (auth.isLoggedIn ? '-in' : '-out'),
                }"
                data-qa="mail already exists info box"
                error
            >
                Die eingegebene E-Mail-Adresse ist mit einem registrierten Account verknüpft.
                <WebOnly>
                    <AppLink
                        :to="alreadyExistsPromptHref"
                        neutral
                        underline
                        data-gtm-element="AP_F: Mail Validation Box Link"
                    >
                        Bitte melde dich mit dem Account an<!-- avoid whitespace -->
                    </AppLink>, um fortzufahren, oder verwende eine andere E-Mail-Adresse.
                </WebOnly>
                <WebViewOnly>
                    Bitte schließe das Bewerbungsformular und melde dich mit dem Account an,
                    um fortzufahren, oder verwende eine andere E-Mail-Adresse.
                </WebViewOnly>
            </AppInfoBox>
            <FormMessage
                v-if="v$.email.$error && !v$.email.$pending"
                type="error"
                data-qa="email form message"
            >
                <template v-if="v$.email.maxLength.$invalid">
                    Bitte gib eine korrekte E-Mail-Adresse ein, mit max. {{ MAX_EMAIL_LENGTH }} Zeichen.
                </template>
                <template v-else-if="v$.email.required.$invalid || v$.email.email.$invalid">
                    Bitte gib eine korrekte E-Mail-Adresse ein.
                </template>
            </FormMessage>
        </template>
    </FormElement>
</template>

<script setup>
import { helpers, maxLength, required } from '@vuelidate/validators';

import useVuelidate from '@vuelidate/core';
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { useDebounce } from '@vueuse/core';
import { defaultRegisterStateBuilder } from '../../../utils/stateBuilder';
import { email as emailValidator, validateIf } from '../../../utils/validators';
import { MAX_EMAIL_LENGTH } from '../../../utils/constants/validationLimits';

import FormElement from '../../molecules/form/FormElement.vue';
import FormInput from '../../atoms/form/FormInput.vue';
import FormLabel from '../../atoms/form/FormLabel.vue';
import FormMessage from '../../atoms/form/FormMessage.vue';
import { useProfile } from '../../../stores/profile-store';
import { useApplicationStore } from '../../../stores/application-store';
import { usePayloadStore } from '../../../stores/payload-store';
import { isEmailAvailable } from '../../../services/user';
import AppInfoBox from '../../molecules/app/AppInfoBox.vue';
import AppLink from '../../atoms/app/AppLink.vue';
import { useAuth } from '../../../stores/auth-store';
import WebOnly from '../../webview/WebOnly.vue';
import WebViewOnly from '../../webview/WebViewOnly.vue';

const props = defineProps({
    debounceTime: {
        default: 250,
        type: Number,
    },
});

const isInputFocus = ref(false);
const isHintInteraction = ref(false);

const profile = useProfile();
const auth = useAuth();
const route = useRoute();
const payload = usePayloadStore();
const application = useApplicationStore();
const { email: rawEmail } = storeToRefs(application);
const email = useDebounce(rawEmail, props.debounceTime);

const isActive = computed(() => isInputFocus.value || isHintInteraction.value);

const shouldValidateMailAvailable = computed(() => profile.email !== rawEmail.value);
const validations = {
    email: {
        required,
        email: emailValidator,
        maxLength: maxLength(MAX_EMAIL_LENGTH),
        available: helpers.withAsync(validateIf(shouldValidateMailAvailable, isEmailAvailable)),
    },
};

const v$ = useVuelidate(validations, { email });

const showEmailAlreadyInUseBox = computed(() => v$.value.email.available.$invalid);

/**
 * focus(), blur(), mouseEnter() and mouseLeave() are here in order to make
 * the component "self-aware" when the user is interacting with it.
 * The component should be considered as active when the input field is focused
 * or the pointer is over the link, so the click on that link will not trigger the validation
 * nor hide the hint due to input focus lose.
 */
function focus() {
    isInputFocus.value = true;
}

function mouseEnter() {
    isHintInteraction.value = true;
}

function mouseLeave() {
    isHintInteraction.value = false;
}

function blur() {
    isInputFocus.value = false;
    // Trigger the validation on blur only when the component is not considered as active.
    // Otherwise, the validation will be triggered when the user click on the link within the hint.
    if (!isActive.value) {
        v$.value.email.$touch();
    }

    application.updateCvRenderData();
}

const router = useRouter();
// Send logged-out users to login, log logged-in users out, send them to log in and redirect to form after.
const alreadyExistsPromptHref = computed(() => {
    if (!auth.isLoggedIn) {
        return router.resolve({
            name: 'login',
        });
    }

    return router.resolve({
        name: 'logout',
        query: {
            redirectUrl: new URL(router.resolve({
                name: 'login',
                query: {
                    redirectUrl: new URL(route.fullPath, window.location.origin).href,
                    uuid: payload.atsUserUuid,
                    payload: payload.encryptedPayload,
                    registerState:
                            defaultRegisterStateBuilder(payload.atsUserUuid, payload.encryptedPayload),
                },
            }).href, window.location.origin).href,
        },
    });
});
</script>

<style lang="scss">
@import '~@karriereat/global-styles/scss/settings/spacing';

.o-formEmailLoginHint {
    &__input {
        &--active {
            border-radius: var(--k-rounded) var(--k-rounded) 0 0;
        }
    }

    &__hint {
        padding: $k-spacing--m;
        border-top: none;
        border-radius: 0 0 var(--k-rounded) var(--k-rounded);
    }

    &__wrap {
        margin: 0;
    }

    &__link {
        color: var(--k-gray-800);

        &--nowrap {
            white-space: nowrap;
        }
    }
}
</style>
